import { gql, useMutation } from "@apollo/client";

import {
  UpdateUserSettingsMutation,
  UpdateUserSettingsMutationVariables,
  User,
  UserSettingsInput,
} from "../../API";
import { UPDATE_USER_SETTINGS } from "../operations/mutations";
import { successNotification } from "../variables/notification";
import { useAuthenticatedUser } from "./useAuthenticatedUser";
import { handleApolloError } from "../utils/handleApolloError";

export const useUpdateUserSettings = () => {
  const [updateSettings, { data, loading }] = useMutation<
    UpdateUserSettingsMutation,
    UpdateUserSettingsMutationVariables
  >(UPDATE_USER_SETTINGS);

  // Use customerId from JWT claims, not from CustomerSelect dropdown
  const { user, customerId } = useAuthenticatedUser();

  const updateUserSettings = (userSettings: UserSettingsInput) => {
    const userId = `U#${user?.attributes?.sub as string}`;

    return updateSettings({
      variables: {
        customerId,
        userId,
        userSettings,
      },
      onCompleted: response => {
        if (response.updateUserSettings) {
          successNotification();
        }
      },
      onError: handleApolloError,
      update: (cache, response): void => {
        cache.modify({
          fields: {
            getUsersByCompany(existing) {
              const newUserRef = cache.writeFragment({
                data: response.data?.updateUserSettings,
                fragment: gql`
                  fragment NewUser on User {
                    customer_id
                    email
                    firstName
                    id
                    lastName
                    userSettings
                  }
                `,
              });

              if (!existing) {
                return response?.data;
              }

              return {
                ...existing,
                items: existing.items.map((item: User | null) => {
                  if (item?.id !== response.data?.updateUserSettings?.id) {
                    return item;
                  }

                  return newUserRef;
                }),
              };
            },
          },
        });
      },
    });
  };

  return { updateUserSettings, data, loading };
};
